import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, Plus, Trash2, Shield, User, Filter, X, ChevronDown, ChevronUp, Check } from "lucide-react";

const API = window.location.origin;

const BADGE_OPTIONS = [
  { value: "Transferli", label: "Transferli", bg: "#2563EB" },
  { value: "Kendi Gelir", label: "Kendi Gelir", bg: "#6B7280" },
  { value: "VIP", label: "VIP", bg: "#7C3AED" },
  { value: "Alkollu", label: "Alkollu", bg: "#EA580C" },
  { value: "Alkolsuz", label: "Alkolsuz", bg: "#16A34A" },
  { value: "Limitsiz", label: "Limitsiz", bg: "#DC2626" },
  { value: "Snack", label: "Snack", bg: "#0891B2" },
  { value: "PVT", label: "Private Table", bg: "#0E7490" },
  { value: "Romantic", label: "Romantic", bg: "#DB2777" },
  { value: "Cam Kenari", label: "Cam Kenari", bg: "#B45309" },
];

const AGENCY_OPTIONS = ["GYG", "VIATOR", "KLOOK", "HEADOUT", "LUFER", "MASAL", "SMALLGRP", "DIGER"];

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("staff");
  const [creating, setCreating] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editAssignment, setEditAssignment] = useState(null);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignUserId, setAssignUserId] = useState("");
  const [assignName, setAssignName] = useState("");
  const [assignCategories, setAssignCategories] = useState([]);
  const [assignBadges, setAssignBadges] = useState([]);
  const [assignAgencies, setAssignAgencies] = useState([]);
  const [respondioTeammates, setRespondioTeammates] = useState([]);
  const [agentMapping, setAgentMapping] = useState({});
  const [allProducts, setAllProducts] = useState([]);
  const [assignProducts, setAssignProducts] = useState([]);

  const token = localStorage.getItem("auth_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [staffRes, assignRes, catRes] = await Promise.all([
        axios.get(`${API}/api/staff`, { headers }),
        axios.get(`${API}/api/staff/assignments`, { headers }),
        axios.get(`${API}/api/categories`, { headers }).catch(() => ({ data: { categories: [] } })),
      ]);
      setStaff(staffRes.data.staff || []);
      setAssignments(assignRes.data.assignments || []);
      setCategories(catRes.data?.categories || catRes.data || []);
      // Build agent mapping from staff data
      const mapping = {};
      (staffRes.data.staff || []).forEach(s => { if (s.respondio_agent_id) mapping[s.id] = s.respondio_agent_id; });
      setAgentMapping(mapping);
      // Fetch Respond.io teammates
      try {
        const tmRes = await axios.get(`${API}/api/respondio/teammates`, { headers });
        setRespondioTeammates(tmRes.data.teammates || []);
      } catch { setRespondioTeammates([]); }
      // Fetch unique product names
      try {
        const prodRes = await axios.get(`${API}/api/products/unique-names`, { headers });
        setAllProducts(prodRes.data.products || []);
      } catch { setAllProducts([]); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createStaff = async () => {
    if (!newEmail || !newPassword || !newName) return;
    setCreating(true);
    try {
      await axios.post(`${API}/api/staff`, { email: newEmail, password: newPassword, name: newName, role: newRole }, { headers });
      setNewEmail(""); setNewPassword(""); setNewName(""); setNewRole("staff"); setShowNewForm(false);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Hata olustu");
    }
    setCreating(false);
  };

  const updateRole = async (id, role) => {
    try {
      await axios.put(`${API}/api/staff/${id}/role`, { role }, { headers });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || "Rol güncellenemedi"); }
  };

  const deleteStaff = async (id) => {
    if (!window.confirm("Bu personeli silmek istediginize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/api/staff/${id}`, { headers });
      fetchData();
    } catch (e) { alert("Silinemedi"); }
  };

  const toggleStaff = async (id) => {
    try {
      await axios.put(`${API}/api/staff/${id}/toggle`, {}, { headers });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const mapRespondioAgent = async (staffId, agentId) => {
    try {
      await axios.put(`${API}/api/staff/${staffId}/respondio-agent`, { respondio_agent_id: agentId || null }, { headers });
      setAgentMapping(prev => ({ ...prev, [staffId]: agentId || null }));
      fetchData();
    } catch (e) { alert("Agent esleme basarisiz"); }
  };

  const saveAssignment = async () => {
    if (!assignUserId || !assignName) return;
    const payload = {
      user_id: parseInt(assignUserId),
      assignment_name: assignName,
      filter_rules: {
        categories: assignCategories,
        badges: assignBadges,
        agencies: assignAgencies,
        products: assignProducts,
      },
    };
    try {
      if (editAssignment) {
        await axios.put(`${API}/api/staff/assignments/${editAssignment.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/api/staff/assignments`, payload, { headers });
      }
      resetAssignForm();
      fetchData();
    } catch (e) { alert("Kaydedilemedi"); }
  };

  const deleteAssignment = async (id) => {
    try {
      await axios.delete(`${API}/api/staff/assignments/${id}`, { headers });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const openEditAssignment = (a) => {
    setEditAssignment(a);
    setAssignUserId(String(a.user_id));
    setAssignName(a.assignment_name);
    setAssignCategories(a.filter_rules?.categories || []);
    setAssignBadges(a.filter_rules?.badges || []);
    setAssignAgencies(a.filter_rules?.agencies || []);
    setAssignProducts(a.filter_rules?.products || []);
    setShowAssignForm(true);
  };

  const resetAssignForm = () => {
    setEditAssignment(null);
    setAssignUserId("");
    setAssignName("");
    setAssignCategories([]);
    setAssignBadges([]);
    setAssignAgencies([]);
    setAssignProducts([]);
    setShowAssignForm(false);
  };

  const toggleItem = (arr, setArr, item) => {
    setArr(prev => prev.includes(item) ? prev.filter(x => x !== item) : [...prev, item]);
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Yukleniyor...</div>;

  return (
    <div className="min-h-screen" style={{ maxWidth: "100vw", overflowX: "hidden" }}>
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-blue-400" />
          <div>
            <h1 className="text-lg font-bold text-white">Personel Yonetimi</h1>
            <p className="text-xs text-slate-500">Gorevli olustur, filtre kurallari ata</p>
          </div>
        </div>
      </div>

      <div className="page-body space-y-6">
        {/* Staff List */}
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">Personeller</h2>
            <button onClick={() => setShowNewForm(!showNewForm)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-lg transition-colors" data-testid="add-staff-btn">
              <Plus className="w-3.5 h-3.5" /> Yeni Personel
            </button>
          </div>

          {showNewForm && (
            <div className="bg-slate-800/50 rounded-lg p-4 mb-4 border border-slate-700" data-testid="new-staff-form">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
                <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Isim" className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 placeholder:text-slate-500" data-testid="staff-name-input" />
                <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Email" className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 placeholder:text-slate-500" data-testid="staff-email-input" />
                <input value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Sifre" type="password" className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 placeholder:text-slate-500" data-testid="staff-password-input" />
                <select value={newRole} onChange={e => setNewRole(e.target.value)} className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2" data-testid="staff-role-select">
                  <option value="staff">Garson</option>
                  <option value="manager">Manager</option>
                  <option value="super_admin">Süper Admin</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button onClick={createStaff} disabled={creating || !newName || !newEmail || !newPassword} className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg" data-testid="save-staff-btn">
                  {creating ? "Kaydediliyor..." : "Kaydet"}
                </button>
                <button onClick={() => setShowNewForm(false)} className="px-4 py-2 bg-slate-700 text-slate-300 text-xs rounded-lg">Iptal</button>
              </div>
            </div>
          )}

          {staff.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">Henuz personel eklenmemis</div>
          ) : (
            <div className="space-y-2">
              {staff.map(s => (
                <div key={s.id} className={`p-3 rounded-lg border ${s.active ? "bg-slate-800/30 border-slate-700" : "bg-slate-900/50 border-slate-800 opacity-60"}`} data-testid={`staff-row-${s.id}`}>
                  <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${s.active ? "bg-blue-600/20 text-blue-400" : "bg-slate-700 text-slate-500"}`}>
                    {(s.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white">{s.name}</div>
                    <div className="text-xs text-slate-500">{s.email}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${s.active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                    {s.active ? "Aktif" : "Pasif"}
                  </span>
                  <select value={(s.role === "manager") ? "manager" : (s.role === "super_admin" || s.role === "admin") ? "super_admin" : "staff"} onChange={(e) => updateRole(s.id, e.target.value)}
                    className="bg-slate-700 border border-slate-600 text-white text-[11px] rounded px-2 py-1" data-testid={`staff-role-${s.id}`}
                    title="Kullanıcı rolü">
                    <option value="staff">Garson</option>
                    <option value="manager">Manager</option>
                    <option value="super_admin">Süper Admin</option>
                  </select>
                  <button onClick={() => toggleStaff(s.id)} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors" title={s.active ? "Devre disi birak" : "Aktif et"}>
                    <Shield className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteStaff(s.id)} className="p-1.5 hover:bg-red-500/20 rounded text-slate-500 hover:text-red-400 transition-colors" data-testid={`delete-staff-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                  </div>
                  {/* Respond.io Agent Mapping */}
                  {respondioTeammates.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-700/50">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-500 shrink-0">Respond.io Agent:</span>
                        <select
                          value={s.respondio_agent_id || ""}
                          onChange={(e) => mapRespondioAgent(s.id, e.target.value)}
                          className="flex-1 bg-slate-700 border border-slate-600 text-white text-[11px] rounded px-2 py-1"
                          data-testid={`agent-select-${s.id}`}
                        >
                          <option value="">Eslenmemis</option>
                          {respondioTeammates.map(tm => (
                            <option key={tm.id} value={tm.id}>{tm.name || tm.email || `Agent #${tm.id}`}</option>
                          ))}
                        </select>
                        {s.respondio_agent_id && <span className="text-[10px] text-green-400 shrink-0">Aktif</span>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assignments */}
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-white uppercase tracking-wider">Gorev Atamalari</h2>
            <button onClick={() => { resetAssignForm(); setShowAssignForm(true); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium rounded-lg transition-colors" data-testid="add-assignment-btn">
              <Filter className="w-3.5 h-3.5" /> Yeni Atama
            </button>
          </div>

          {showAssignForm && (
            <div className="bg-slate-800/50 rounded-lg p-4 mb-4 border border-purple-500/30" data-testid="assignment-form">
              <div className="space-y-3">
                {/* Staff select */}
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Personel</label>
                  <select value={assignUserId} onChange={e => setAssignUserId(e.target.value)} className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2" data-testid="assign-user-select">
                    <option value="">Personel secin...</option>
                    {staff.filter(s => s.active).map(s => <option key={s.id} value={s.id}>{s.name} ({s.email})</option>)}
                  </select>
                </div>
                {/* Assignment name */}
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Atama Adi</label>
                  <input value={assignName} onChange={e => setAssignName(e.target.value)} placeholder="orn: Dinner VIP Transferli" className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-lg px-3 py-2 placeholder:text-slate-500" data-testid="assign-name-input" />
                </div>
                {/* Categories */}
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Kategoriler</label>
                  <div className="flex flex-wrap gap-1.5">
                    {(Array.isArray(categories) ? categories : []).map(c => (
                      <button key={c.id || c.slug} onClick={() => toggleItem(assignCategories, setAssignCategories, c.slug || c.name)} className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${assignCategories.includes(c.slug || c.name) ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                      {c.name}
                    </button>
                    ))}
                  </div>
                </div>
                {/* Badges */}
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Opsiyon Filtreleri</label>
                  <div className="flex flex-wrap gap-1.5">
                    {BADGE_OPTIONS.map(b => (
                      <button key={b.value} onClick={() => toggleItem(assignBadges, setAssignBadges, b.value)} className="px-2.5 py-1 rounded-lg text-xs font-bold border transition-all" style={{ backgroundColor: assignBadges.includes(b.value) ? b.bg : "transparent", borderColor: assignBadges.includes(b.value) ? b.bg : "#475569", color: assignBadges.includes(b.value) ? "#fff" : "#94a3b8" }}>
                        {b.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Agencies */}
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Acentalar</label>
                  <div className="flex flex-wrap gap-1.5">
                    {AGENCY_OPTIONS.map(a => (
                      <button key={a} onClick={() => toggleItem(assignAgencies, setAssignAgencies, a)} className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${assignAgencies.includes(a) ? "bg-emerald-600 border-emerald-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Product Names */}
                {allProducts.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1 block">Urun Opsiyonlari (Tam Ad)</label>
                    <div className="max-h-48 overflow-y-auto space-y-1 bg-slate-900/30 rounded-lg p-2 border border-slate-700">
                      {allProducts.map(p => (
                        <label key={p} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${assignProducts.includes(p) ? "bg-indigo-600/20 border border-indigo-500/40" : "hover:bg-slate-800"}`}>
                          <input type="checkbox" checked={assignProducts.includes(p)} onChange={() => toggleItem(assignProducts, setAssignProducts, p)} className="w-3.5 h-3.5 accent-indigo-500 shrink-0" />
                          <span className={`text-xs ${assignProducts.includes(p) ? "text-white font-medium" : "text-slate-400"}`}>{p}</span>
                        </label>
                      ))}
                    </div>
                    {assignProducts.length > 0 && <div className="text-[10px] text-indigo-400 mt-1">{assignProducts.length} urun secildi</div>}
                  </div>
                )}
                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  <button onClick={saveAssignment} disabled={!assignUserId || !assignName} className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg" data-testid="save-assignment-btn">
                    {editAssignment ? "Guncelle" : "Kaydet"}
                  </button>
                  <button onClick={resetAssignForm} className="px-4 py-2 bg-slate-700 text-slate-300 text-xs rounded-lg">Iptal</button>
                </div>
              </div>
            </div>
          )}

          {assignments.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">Henuz atama yapilmamis</div>
          ) : (
            <div className="space-y-2">
              {assignments.map(a => {
                const staffMember = staff.find(s => s.id === a.user_id);
                const rules = a.filter_rules || {};
                return (
                  <div key={a.id} className="p-3 rounded-lg bg-slate-800/30 border border-slate-700" data-testid={`assignment-row-${a.id}`}>
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-purple-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white">{a.assignment_name}</div>
                        <div className="text-xs text-slate-500">{staffMember?.name || `ID:${a.user_id}`}</div>
                      </div>
                      <button onClick={() => openEditAssignment(a)} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white text-xs">Duzenle</button>
                      <button onClick={() => deleteAssignment(a.id)} className="p-1.5 hover:bg-red-500/20 rounded text-slate-500 hover:text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {/* Filter tags */}
                    <div className="flex flex-wrap gap-1 mt-2 ml-7">
                      {(rules.categories || []).map(c => <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-medium">{c}</span>)}
                      {(rules.badges || []).map(b => {
                        const bo = BADGE_OPTIONS.find(x => x.value === b);
                        return <span key={b} className="text-[10px] px-1.5 py-0.5 rounded font-bold text-white" style={{ backgroundColor: bo?.bg || "#666" }}>{b}</span>;
                      })}
                      {(rules.agencies || []).map(a => <span key={a} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-medium">{a}</span>)}
                      {(rules.products || []).map(p => <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 font-medium truncate max-w-[200px]" title={p}>{p}</span>)}
                      {!(rules.categories?.length || rules.badges?.length || rules.agencies?.length || rules.products?.length) && (
                        <span className="text-[10px] text-slate-500">Filtre yok (tum rezervasyonlari gorur)</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="glass-card p-4">
          <div className="text-xs text-slate-500 space-y-1">
            <p>Personeller kendi email/sifreleriyle giris yapar ve sadece atandiklari rezervasyonlari gorur.</p>
            <p>Filtre kurallari: Kategori + Opsiyon Tipi + Acenta kombinasyonu. Bos birakilan alan filtrelenmez.</p>
            <p>Personel operasyon sayfasinda "Hepsini Goster" ile tum rezervasyonlari gorebilir.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
